// src/DashboardLayout.js
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Box, Divider, Button, Avatar, Tooltip
} from '@mui/material';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import ChangePasswordDialog from './ChangePasswordDialog';

import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon    from '@mui/icons-material/Logout';
import LinkIcon      from '@mui/icons-material/Link';

const drawerWidth = 240;

const ROLE_LABELS = {
    administrador: 'Administrador',
    gerente:       'Gerente',
    autonomo:      'Parceiro Autônomo',
    imobiliaria:   'Imobiliária',
};

/**
 * @param {object}   userProfile           - Perfil do usuário logado (nome, tipoUsuario, primeiroAcesso…)
 * @param {function} onFirstAccessComplete - Callback após troca da senha temporária
 */
function DashboardLayout({ children, menuItems, pageTitle, userProfile, onFirstAccessComplete }) {
    const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

    const forcedPasswordChange = userProfile?.primeiroAcesso === true;
    const displayName  = userProfile?.nome || userProfile?.email || '';
    const roleLabel    = ROLE_LABELS[userProfile?.tipoUsuario] || '';
    const avatarLetter = displayName.charAt(0).toUpperCase();

    return (
        <Box sx={{ display: 'flex' }}>

            {/* ── AppBar ─────────────────────────────────────────────────── */}
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    <Box
                        component="img"
                        src="/logo.png"
                        alt="MBigucci"
                        sx={{ height: 40, mr: 2, bgcolor: 'white', p: '5px', borderRadius: '4px' }}
                    />

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        Gestão de Parceiros
                    </Typography>

                    {/* Conta logada */}
                    {userProfile && (
                        <Tooltip title={userProfile.email || ''} placement="bottom">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 2 }}>
                                <Avatar sx={{
                                    width: 32, height: 32,
                                    bgcolor: 'rgba(255,255,255,0.25)',
                                    fontSize: '0.9rem', fontWeight: 'bold'
                                }}>
                                    {avatarLetter}
                                </Avatar>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'inherit' }}>
                                        {displayName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.2, color: 'inherit' }}>
                                        {roleLabel}
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>
                    )}

                    <Button color="inherit" onClick={() => setIsPasswordDialogOpen(true)} sx={{ mr: 1 }}>
                        Alterar Senha
                    </Button>
                    <Button color="inherit" onClick={() => signOut(auth)}>Sair</Button>
                </Toolbar>
            </AppBar>

            {/* ── Drawer lateral ─────────────────────────────────────────── */}
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    ['& .MuiDrawer-paper']: { width: drawerWidth, boxSizing: 'border-box' },
                }}
            >
                <Toolbar />
                <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <List>
                        {menuItems && menuItems.map((item) => (
                            <ListItem
                                key={item.text}
                                disablePadding
                                component={RouterLink}
                                to={item.path}
                                sx={{ color: 'inherit', textDecoration: 'none' }}
                            >
                                <ListItemButton>
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                </ListItemButton>
                            </ListItem>
                        ))}

                        <Divider sx={{ my: 1 }} />

                        <ListItem
                            disablePadding
                            component="a"
                            href="https://linktr.ee/Construtorambigucci?utm_source=linktree_profile_share"
                            target="_blank"
                            sx={{ color: 'inherit', textDecoration: 'none' }}
                        >
                            <ListItemButton>
                                <ListItemIcon><LinkIcon color="primary" /></ListItemIcon>
                                <ListItemText primary="Links Úteis MBigucci" />
                            </ListItemButton>
                        </ListItem>
                    </List>

                    <Box sx={{ flexGrow: 1 }} />
                    <Divider />

                    <List>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => setIsPasswordDialogOpen(true)}>
                                <ListItemIcon><LockResetIcon /></ListItemIcon>
                                <ListItemText primary="Alterar Senha" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => signOut(auth)}>
                                <ListItemIcon><LogoutIcon /></ListItemIcon>
                                <ListItemText primary="Sair" />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>

            {/* ── Conteúdo principal ─────────────────────────────────────── */}
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Toolbar />
                {pageTitle && (
                    <>
                        <Typography variant="h4" gutterBottom>{pageTitle}</Typography>
                        <Divider sx={{ mb: 3 }} />
                    </>
                )}
                {children}
            </Box>

            {/* ── Diálogo de senha (normal ou forçado) ───────────────────── */}
            <ChangePasswordDialog
                open={forcedPasswordChange || isPasswordDialogOpen}
                onClose={() => setIsPasswordDialogOpen(false)}
                forced={forcedPasswordChange}
                onFirstAccessComplete={onFirstAccessComplete}
            />
        </Box>
    );
}

export default DashboardLayout;
